import { deriveHKDFKey } from './hkdf';
import { encryptAES, decryptAES, concatNonceAndCiphertext, splitNonceAndCiphertext } from './aes-gcm';
import { deriveX25519SharedSecret, generateX25519KeyPair, importPrivateKey, importPublicKey } from './keys';

const WRAP_INFO = 'atten-wrap-v1';

export async function wrapKeyForRecipient(
  keyToWrap: CryptoKey,
  recipientPublicKeyData: ArrayBuffer,
  _senderPrivateKeyData: ArrayBuffer
): Promise<{ wrappedKey: ArrayBuffer; ephemeralPublicKey: ArrayBuffer }> {
  const recipientPublicKey = await importPublicKey(recipientPublicKeyData);
  const ephemeralKeyPair = await generateX25519KeyPair();

  const ephemeralPublicKey = ephemeralKeyPair.publicKey;

  const sharedSecret = await deriveX25519SharedSecret(ephemeralKeyPair.privateKey, recipientPublicKey);

  const wrapKey = await deriveHKDFKey(
    sharedSecret,
    new Uint8Array(0).buffer as ArrayBuffer,
    WRAP_INFO
  );

  const { ciphertext, nonce } = await encryptAES(wrapKey, await crypto.subtle.exportKey('raw', keyToWrap));
  const wrappedKey = concatNonceAndCiphertext(nonce, ciphertext);

  return { wrappedKey, ephemeralPublicKey };
}

export async function unwrapKeyFromSender(
  wrappedKeyData: ArrayBuffer,
  ephemeralPublicKeyData: ArrayBuffer,
  recipientPrivateKeyData: ArrayBuffer
): Promise<CryptoKey> {
  const ephemeralPublicKey = await importPublicKey(ephemeralPublicKeyData);
  const recipientPrivateKey = await importPrivateKey(recipientPrivateKeyData);

  const sharedSecret = await deriveX25519SharedSecret(recipientPrivateKey, ephemeralPublicKey);

  const wrapKey = await deriveHKDFKey(
    sharedSecret,
    new Uint8Array(0).buffer as ArrayBuffer,
    WRAP_INFO
  );

  const { nonce, ciphertext } = splitNonceAndCiphertext(wrappedKeyData);
  const rawKey = await decryptAES(wrapKey, ciphertext, nonce);

  return crypto.subtle.importKey(
    'raw',
    rawKey,
    { name: 'AES-GCM' },
    true,
    ['encrypt', 'decrypt']
  );
}
